import { FC, useMemo } from 'react'
import { cn } from '../../../lib/utils'
import { MessageMarkdown } from '../shared/MessageMarkdown'
import { MessageActions } from '../MessageActions'
import { ToolCard } from './ToolCard'
import { InlineCanvas } from './InlineCanvas'
import { ThinkingPanel } from '../ThinkingPanel'
import { getAgentPersona } from './AgentSelector'
import { ConsultPill } from './ConsultPill'
import { stripRelay } from '../../../hooks/usePatientPanel'
import type { AgentChatMessage } from '../../../contexts/ChatContext'

// A dangling marker prefix that has not yet finished streaming. stripRelay only
// removes a COMPLETE `<!-- RELAY: {...} -->`; while tokens arrive a partial
// `<!--`/`<!-- RELAY...` would otherwise flash before the closing `-->` lands.
const DANGLING_MARKER_RE = /<!--[\s\S]*$/

interface CasePanelMessageProps {
  message: AgentChatMessage
  agentId: string
  onOpenCanvas?: (tab: string) => void
  onExpandToPanel?: (content: string, durationMs?: number) => void
  onEdit?: () => void
  isLoading?: boolean
}

/**
 * Single chat message bubble for the case panel.
 * User messages right-aligned, assistant messages left-aligned with agent persona.
 */
export const CasePanelMessage: FC<CasePanelMessageProps> = ({
  message,
  agentId,
  onOpenCanvas,
  onExpandToPanel,
  onEdit,
  isLoading
}) => {
  const isUser = message.role === 'user'
  const isError = message.role === 'error'
  const persona = getAgentPersona(message.agentId || agentId)
  const rawContent = typeof message.content === 'string' ? message.content : String(message.content ?? '')
  const content = useMemo(() => {
    if (isUser) return rawContent
    // Always strip a finalized relay marker; while still streaming, also mask a
    // dangling (not-yet-closed) marker prefix so `<!--` never flashes mid-stream.
    const stripped = stripRelay(rawContent) ?? ''
    return message.isStreaming ? stripped.replace(DANGLING_MARKER_RE, '').trimEnd() : stripped
  }, [rawContent, isUser, message.isStreaming])
  // #267: any tool that built its result from an over-cap (bounded) chart read
  // flags the whole message so the clinician sees a non-alarmist completeness note.
  const hasIncompleteExtraction = !isUser && !isError && (message.tools?.some(t => t.extractionDegraded) ?? false)

  return (
    <div className={cn('flex items-start gap-3 mb-4 animate-in fade-in-0', isUser ? 'flex-row-reverse' : '')}>
      {/* Avatar */}
      {isUser ? (
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-[10px] font-bold text-white">MD</span>
        </div>
      ) : !isError ? (
        <div
          className={cn(
            'w-7 h-7 rounded-full bg-gradient-to-br flex items-center justify-center flex-shrink-0 mt-0.5',
            persona.accentFrom, persona.accentTo
          )}
        >
          <span className="text-[10px] font-bold text-white">{persona.avatar}</span>
        </div>
      ) : null}

      <div className={cn('max-w-[80%] flex flex-col', isUser ? 'items-end' : 'items-start')}>
        {/* Agent name label */}
        {!isUser && !isError && (
          <span className="text-[11px] font-medium text-muted-foreground mb-0.5 px-1">{persona.name}</span>
        )}
        {/* Reasoning chain (live + persisted). Present (even empty) ⇒ thinking occurred. */}
        {!isUser && !isError && message.thinkingContent !== undefined && (
          // isThinking is true only during an active reasoning phase, so the live "Reasoning…"
          // indicator persists across multi-phase reasoning and flips to "View Reasoning (Xs)"
          // once a phase completes / on reload (persisted messages never set isThinking).
          <ThinkingPanel
            className="mb-1 w-full"
            thinkingContent={message.thinkingContent}
            durationMs={message.thinkingDurationMs}
            isStreaming={message.isThinking === true}
            onExpandToPanel={
              message.thinkingContent && onExpandToPanel
                ? () => onExpandToPanel(message.thinkingContent!, message.thinkingDurationMs)
                : undefined
            }
          />
        )}
        {/* Consult pills — rendered inside Casey's turn, above the synthesis bubble.
            One per consult leg (a multi-hop turn stacks them in dispatch order). */}
        {!isUser && !isError && message.consult && message.consult.length > 0 && (
          <div className="w-full space-y-1.5">
            {message.consult.map((c, idx) => (
              <ConsultPill key={idx} consult={c} />
            ))}
          </div>
        )}
        {/* Message bubble — only render when the bubble itself has text: a user/error message
            or assistant prose. A canvas-only assistant turn (content === '', canvasUpdates set)
            must NOT draw an empty bubble shell — the canvas renders via InlineCanvas below (#196),
            mirroring the thinking-only invariant (no empty card when there is nothing to show). */}
        {(isUser || isError || content) && (
        <div
          className={cn(
            'relative rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
            isUser
              ? 'bg-primary text-primary-foreground rounded-tr-sm shadow-lg'
              : isError
              ? 'bg-destructive/10 text-destructive border border-destructive/20 rounded-tl-sm'
              : 'bg-card border border-border rounded-tl-sm'
          )}
        >
          {/* Message content */}
          {isUser ? (
            <div className="whitespace-pre-wrap">{content}</div>
          ) : message.canvasUpdates && message.canvasUpdates.length > 0 ? (
            <div className="text-muted-foreground text-sm italic">{content}</div>
          ) : content ? (
            <MessageMarkdown content={content} />
          ) : null}
        </div>
        )}

        {/* Copy action — assistant messages with content (MessageActions renders Copy for role="assistant").
            Excludes fromPipeline messages, matching the ToolCard guard below — pipeline-automated
            responses carry no actions per the design contract (#168). */}
        {!isUser && !isError && !message.fromPipeline && content && (
          <MessageActions
            role="assistant"
            content={content}
            isGenerating={isLoading}
            className="mt-1"
          />
        )}

        {/* Edit action — user messages (MessageActions renders Edit for role="user") */}
        {isUser && onEdit && (
          <MessageActions
            role="user"
            content={content}
            isGenerating={isLoading}
            onEdit={onEdit}
            className="mt-1"
          />
        )}

        {/* Inline canvas preview */}
        {!isUser && !isError && message.canvasUpdates && message.canvasUpdates.length > 0 && (
          <div className="w-full mt-1">
            <InlineCanvas canvasUpdates={message.canvasUpdates} onOpenCanvas={onOpenCanvas} />
          </div>
        )}

        {/* Tool transparency card — hidden for pipeline-automated responses */}
        {!isUser && !isError && !message.fromPipeline && message.tools && message.tools.length > 0 && (
          <ToolCard tools={message.tools} />
        )}

        {/* Extraction-completeness note — a chart read hit the read cap, so this
            note may be built from a partial chart (#267). Non-alarmist (amber, not error). */}
        {hasIncompleteExtraction && (
          <div
            className="flex items-center gap-1.5 mt-1 px-2 py-1 rounded-lg bg-clinical-amber/10 border border-clinical-amber/20 text-[11px] text-clinical-amber"
            title="A chart read exceeded the single-read cap, so this response may not reflect the full chart. Review the source chart for anything past the cap."
          >
            <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <span>Extraction may be incomplete — chart exceeded the read cap</span>
          </div>
        )}


        {/* Timestamp */}
        <div className={cn('text-[10px] text-muted-foreground mt-1 px-1', isUser ? 'text-right' : 'text-left')}>
          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  )
}
